import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Field, Input, Select, Empty } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Upload, Search, ArrowLeftRight, Download, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  uan: "", name: "", emp_code: "", gender: "M", mobile: "", dob: "", esi_number: "", pan: "",
  bank_name: "", bank_account: "", ifsc: "", client_id: "", contract_id: "", designation: "",
  wage_category: "", wage_type: "central", state: "", joining_date: "",
  pf_applicable: true, esi_applicable: true, pt_applicable: true, active: true,
};

export default function Employees() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryInactivityMonth =
    searchParams.get("inactivity_month") || "";

  const queryInactivity =
    searchParams.get("inactivity") || "";

  const queryClient =
    searchParams.get("client_id") || "";
  const [emps, setEmps] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [filters, setFilters] = useState({
    q: "",
    client_id: queryClient,
    designation: "",
    wage_type: "",
    active: "",
  });
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [transferEmp, setTransferEmp] = useState(null);
  const [selected, setSelected] = useState([]);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);

  const load = () => {
    const params = {};

    Object.entries(filters).forEach(
      ([k, v]) => {
        if (v) params[k] = v;
      }
    );

    if (queryInactivityMonth) {
      params.inactivity_month =
        queryInactivityMonth;
    }

    api
      .get("/employees", { params })
      .then((r) => {
        const rows =
          queryInactivity === "1"
            ? r.data.filter(
                (e) => e.inactivity_alert
              )
            : r.data;

        setEmps(rows);
      });

    setSelected([]);
  };
  useEffect(() => {
    load();
  }, [
    filters,
    queryInactivityMonth,
    queryInactivity,
  ]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  useEffect(() => {
    if (form.client_id) api.get(`/clients/${form.client_id}/contracts`).then((r) => setContracts(r.data));
    else setContracts([]);
  }, [form.client_id]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e, esi_applicable: e.esi_applicable ?? false }); setEditId(e.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.value === "true" });

  const save = async () => {
    try {
      if (editId) await api.put(`/employees/${editId}`, form);
      else await api.post("/employees", form);
      toast.success("Employee saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkStatus = async (active) => {
    try { await api.post("/employees/bulk-status", { ids: selected, active }); toast.success(`${selected.length} updated`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const cname = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Employees" subtitle="Master data keyed by unique UAN"
        actions={<>
          <Btn variant="outline" onClick={() => downloadFile(`/employees/master-export${filters.client_id ? `?client_id=${filters.client_id}` : ""}`, "employee_master.xlsx")} data-testid="master-export-btn"><Download size={16} /> Master Excel</Btn>
          <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-employees-btn"><Upload size={16} /> Bulk Import</Btn>
          <Btn onClick={openNew} data-testid="add-employee-btn"><Plus size={16} /> Add Employee</Btn>
        </>} />

      <Card className="p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input placeholder="Search UAN / name" data-testid="employee-search"
              value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <Select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })} data-testid="filter-client">
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Input placeholder="Designation" value={filters.designation} onChange={(e) => setFilters({ ...filters, designation: e.target.value })} />
          <Select value={filters.wage_type} onChange={(e) => setFilters({ ...filters, wage_type: e.target.value })}>
            <option value="">Central / State</option><option value="central">Central</option><option value="state">State</option>
          </Select>
          <Select value={filters.active} onChange={(e) => setFilters({ ...filters, active: e.target.value })}>
            <option value="">All Status</option><option value="true">Active</option><option value="false">Inactive</option>
          </Select>
        </div>
      </Card>

      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-blue-50 border border-blue-200 rounded-md px-4 py-2" data-testid="bulk-bar">
          <span className="text-sm font-medium text-blue-900">{selected.length} selected</span>
          <Btn variant="outline" onClick={() => bulkStatus(true)} data-testid="bulk-activate"><CheckCircle2 size={14} /> Activate</Btn>
          <Btn variant="outline" onClick={() => bulkStatus(false)} data-testid="bulk-deactivate"><XCircle size={14} /> Deactivate</Btn>
        </div>
      )}

      <Card className="overflow-hidden">

        {/* Always-visible top horizontal scrollbar */}
        <div
          ref={topScrollRef}
          className="overflow-x-auto overflow-y-hidden border-b border-slate-200"
          onScroll={(e) => {
            if (tableScrollRef.current) {
              tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <div style={{ width: "2500px", height: "14px" }} />
        </div>

        <div
          ref={tableScrollRef}
          className="overflow-x-auto overflow-y-auto max-h-[620px]"
          onScroll={(e) => {
            if (topScrollRef.current) {
              topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          {emps.length ? (
            <table
              className="nk-table min-w-[2500px]"
              data-testid="employees-table"
            >
              <thead>
                <tr>
                  <th className="sticky top-0 left-0 z-40 bg-slate-100 min-w-[220px] w-[220px] shadow-[4px_0_6px_-4px_rgba(0,0,0,0.22)]">
                    Name
                  </th>
                  <th className="sticky top-0 z-30 bg-slate-100 min-w-[48px] w-[48px]">
                    <input
                      type="checkbox"
                      checked={emps.length > 0 && selected.length === emps.length}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked ? emps.map((x) => x.id) : []
                        )
                      }
                      data-testid="select-all"
                    />
                  </th>
                  <th className="sticky top-0 z-30 bg-slate-100 min-w-[150px]">UAN</th>
                  <th className="sticky top-0 z-30 bg-slate-100 min-w-[130px]">Employee Code</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Gender</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Mobile</th>
                  <th className="sticky top-0 z-30 bg-slate-100">DOB</th>
                  <th className="sticky top-0 z-30 bg-slate-100">PAN</th>
                  <th className="sticky top-0 z-30 bg-slate-100">ESI Number</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Designation</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Client</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Bank Name</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Bank Account</th>
                  <th className="sticky top-0 z-30 bg-slate-100">IFSC</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Wage Type</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Wage Category</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Daily Rate</th>
                  <th className="sticky top-0 z-30 bg-slate-100">State</th>
                  <th className="sticky top-0 z-30 bg-slate-100">DOJ</th>
                  <th className="sticky top-0 z-30 bg-slate-100">PF</th>
                  <th className="sticky top-0 z-30 bg-slate-100">ESI</th>
                  <th className="sticky top-0 z-30 bg-slate-100">PT</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Status</th>
                  <th className="sticky top-0 z-30 bg-slate-100">Actions</th>
                </tr>
              </thead>

              <tbody>
                {emps.map((e) => (
                  <tr key={e.id}>
                    <td className="sticky left-0 z-20 bg-white font-medium whitespace-nowrap min-w-[220px] w-[220px] shadow-[4px_0_6px_-4px_rgba(0,0,0,0.22)]">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/employees/${e.id}`)
                        }
                        className="text-left font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                        title="Open Employee Profile"
                      >
                        {e.name || "—"}
                      </button>
                    </td>

                    <td className="min-w-[48px] w-[48px]">
                      <input
                        type="checkbox"
                        checked={selected.includes(e.id)}
                        onChange={() => toggleSel(e.id)}
                      />
                    </td>

                    <td className="font-semibold whitespace-nowrap min-w-[150px]">
                      {e.uan || "—"}
                    </td>

                    <td className="whitespace-nowrap min-w-[130px]">
                      {e.emp_code || e.employee_no || "—"}
                    </td>

                    <td>{e.gender || "—"}</td>

                    <td className="whitespace-nowrap">
                      {e.mobile || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.dob || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.pan || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.esi_number || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.designation || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {cname(e.client_id)}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.bank_name || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.bank_account || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.ifsc || "—"}
                    </td>

                    <td className="capitalize whitespace-nowrap">
                      {e.wage_type || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.wage_category || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.source_daily_rate || e.daily_rate || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.state || "—"}
                    </td>

                    <td className="whitespace-nowrap">
                      {e.joining_date || e.effective_date || "—"}
                    </td>

                    <td>{e.pf_applicable ? "Yes" : "No"}</td>
                    <td>{e.esi_applicable ? "Yes" : "No"}</td>
                    <td>{e.pt_applicable ? "Yes" : "No"}</td>

                    <td>
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge
                          status={e.active ? "active" : "inactive"}
                        />

                        {e.inactivity_alert && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-700 whitespace-nowrap"
                            title={
                              `${e.inactivity_reason || "No payroll activity"} — ${e.inactivity_month || ""}`
                            }
                          >
                            <AlertTriangle size={11} />
                            Inactive 1 Month
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        <Btn
                          variant="ghost"
                          onClick={() => setTransferEmp(e)}
                          title="Transfer"
                        >
                          <ArrowLeftRight size={14} />
                        </Btn>

                        <Btn
                          variant="ghost"
                          onClick={() => openEdit(e)}
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No employees found"
              hint="Try changing the filters."
            />
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <Field label="UAN Number"><Input value={form.uan} onChange={set("uan")} data-testid="employee-uan" /></Field>
            <Field label="Employee Name"><Input value={form.name} onChange={set("name")} data-testid="employee-name" /></Field>
            <Field label="Employee Code"><Input value={form.emp_code} onChange={set("emp_code")} /></Field>
            <Field label="Gender"><Select value={form.gender} onChange={set("gender")}><option>M</option><option>F</option><option>Other</option></Select></Field>
            <Field label="Mobile"><Input value={form.mobile} onChange={set("mobile")} /></Field>
            <Field label="ESI Number"><Input value={form.esi_number} onChange={set("esi_number")} /></Field>
            <Field label="PAN"><Input value={form.pan} onChange={set("pan")} /></Field>
            <Field label="Bank Name"><Input value={form.bank_name} onChange={set("bank_name")} /></Field>
            <Field label="Bank Account"><Input value={form.bank_account} onChange={set("bank_account")} /></Field>
            <Field label="IFSC"><Input value={form.ifsc} onChange={set("ifsc")} /></Field>
            <Field label="Client">
              <Select value={form.client_id} onChange={set("client_id")} data-testid="employee-client">
                <option value="">Select</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Contract">
              <Select value={form.contract_id} onChange={set("contract_id")}>
                <option value="">Select</option>{contracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Designation"><Input value={form.designation} onChange={set("designation")} /></Field>
            <Field label="Wage Category"><Input value={form.wage_category} onChange={set("wage_category")} placeholder="Unskilled / Skilled" /></Field>
            <Field label="Central / State"><Select value={form.wage_type} onChange={set("wage_type")}><option value="central">Central</option><option value="state">State</option></Select></Field>
            <Field label="State"><Input value={form.state} onChange={set("state")} /></Field>
            <Field label="Joining Date"><Input type="date" value={form.joining_date} onChange={set("joining_date")} /></Field>
            <Field label="PF Applicable"><Select value={form.pf_applicable ? "true" : "false"} onChange={setBool("pf_applicable")}><option value="true">Yes</option><option value="false">No</option></Select></Field>
            <Field label="ESI Applicable"><Select value={form.esi_applicable ? "true" : "false"} onChange={setBool("esi_applicable")}><option value="true">Yes</option><option value="false">No</option></Select></Field>
            <Field label="PT Applicable"><Select value={form.pt_applicable ? "true" : "false"} onChange={setBool("pt_applicable")}><option value="true">Yes</option><option value="false">No</option></Select></Field>
            <Field label="Status"><Select value={form.active ? "true" : "false"} onChange={setBool("active")}><option value="true">Active</option><option value="false">Inactive</option></Select></Field>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={save} data-testid="save-employee-btn">Save Employee</Btn>
          </div>
        </DialogContent>
      </Dialog>

      {importOpen && <ImportDialog clients={clients} onClose={() => setImportOpen(false)} onDone={load} />}
      {transferEmp && <TransferDialog emp={transferEmp} clients={clients} onClose={() => setTransferEmp(null)} onDone={load} />}
    </div>
  );
}

function TransferDialog({ emp, clients, onClose, onDone }) {
  const [form, setForm] = useState({ client_id: "", contract_id: "", designation: emp.designation || "", wage_category: emp.wage_category || "", wage_type: emp.wage_type || "central", effective_date: new Date().toISOString().slice(0, 10) });
  const [contracts, setContracts] = useState([]);
  useEffect(() => { if (form.client_id) api.get(`/clients/${form.client_id}/contracts`).then((r) => setContracts(r.data)); }, [form.client_id]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.client_id) { toast.error("Select new client"); return; }
    try { await api.post(`/employees/${emp.id}/transfer`, form); toast.success("Employee transferred"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Transfer — {emp.name} ({emp.uan})</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Current client: {clients.find((c) => c.id === emp.client_id)?.name || "—"}. Previous deployment is closed; historical payroll is untouched.</p>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="New Client">
            <Select value={form.client_id} onChange={set("client_id")} data-testid="transfer-client">
              <option value="">Select</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="New Contract">
            <Select value={form.contract_id} onChange={set("contract_id")}>
              <option value="">Select</option>{contracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Designation"><Input value={form.designation} onChange={set("designation")} /></Field>
          <Field label="Wage Category"><Input value={form.wage_category} onChange={set("wage_category")} /></Field>
          <Field label="Central / State"><Select value={form.wage_type} onChange={set("wage_type")}><option value="central">Central</option><option value="state">State</option></Select></Field>
          <Field label="Effective Date"><Input type="date" value={form.effective_date} onChange={set("effective_date")} /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} data-testid="submit-transfer-btn">Transfer</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ clients, onClose, onDone }) {
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("update");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => setResult(null);
  const submit = async (preview) => {
    if (!clientId || !file) { toast.error("Select company and file"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("mode", mode);
    fd.append("preview", String(preview));
    fd.append("preview_token", result?.preview_token || "");
    fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import", fd);
      setResult(data);
      if (!preview) {
        onDone();
        if (data.errors?.length) toast.error("Import needs reconciliation; review the row errors");
        else toast.success(`Updated ${data.updated}; created ${data.created}; unchanged ${data.unchanged}`);
      }
    } catch (e) { reset(); toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const downloadErrors = () => {
    const csv = "Row,Message\n" + (result?.errors || []).map(e => `${e.row},"${String(e.message).replace(/"/g, '""')}"`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "employee_import_errors.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Dialog open onOpenChange={() => { if (!busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Employee Import / Update</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Export Master Excel for one company, edit its existing UAN rows, then validate here. ESI Applicable accepts Yes/No. ESI Number and eligibility are separate fields. Blank cells preserve values; [CLEAR] clears a text field. Transfers use the Transfer workflow.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Company"><Select value={clientId} disabled={busy} onChange={e => { setClientId(e.target.value); reset(); }} data-testid="import-client"><option value="">Select company</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
          <Field label="Import mode"><Select value={mode} disabled={busy} onChange={e => { setMode(e.target.value); reset(); }} data-testid="employee-import-mode"><option value="update">Update existing employees by UAN</option><option value="create">Create new employees only</option></Select></Field>
        </div>
        <Field label="Master Excel / CSV"><Input type="file" disabled={busy} accept=".xlsx,.csv" onChange={e => { setFile(e.target.files[0]); reset(); }} data-testid="import-file" /></Field>
        {result && <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3">
          {result.preview ? <p>Validated {result.total} rows. {result.ready ? "Ready to apply." : "Fix all errors before applying."}</p> : <p>Updated: <b>{result.updated}</b> · Created: <b>{result.created}</b> · Unchanged: <b>{result.unchanged}</b></p>}
          {result.preview_rows?.length > 0 && <div className="max-h-48 overflow-auto mt-2"><table className="nk-table"><thead><tr><th>Excel row</th><th>UAN</th><th>Fields to change</th></tr></thead><tbody>{result.preview_rows.map(r => <tr key={r.row}><td>{r.row}</td><td>{r.uan}</td><td>{r.fields.join(", ") || "No changes"}</td></tr>)}</tbody></table></div>}
          {result.errors?.length > 0 && <div className="mt-2 text-red-700"><Btn variant="outline" onClick={downloadErrors}>Download row errors</Btn><ul className="mt-2">{result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul></div>}
          {!result.preview && <p className="mt-2 text-slate-600">Existing payroll is unchanged. Recalculate an editable payroll run to apply the updated eligibility.</p>}
        </div>}
        <div className="flex justify-end gap-2 mt-2"><Btn variant="outline" disabled={busy} onClick={onClose}>Close</Btn><Btn variant="outline" disabled={busy} onClick={() => submit(true)} data-testid="validate-employee-import">{busy ? "Working…" : "Validate file"}</Btn><Btn disabled={busy || !result?.preview || !result?.ready} onClick={() => submit(false)} data-testid="run-import-btn">Apply validated changes</Btn></div>
      </DialogContent>
    </Dialog>
  );
}
